import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { environment } from '../../environments/environment';
import { MatButtonModule } from '@angular/material/button';
import { SimpleChanges } from '@angular/core';
import { getAccuratePosition } from '../utils';
import { ResizableTextareaComponent } from '../shared/components/resizable-textarea/resizable-textarea.component';

interface CatchResponse {
  catch_count: string;
  catch_id: number;
}

const NOTES_TIMEOUT_MS = 10000;

@Component({
  selector: 'app-fish-catch-button',
  template: `
    <button
      mat-fab
      extended
      class="custom-fish-button"
      (click)="catchFish()"
    >
      <div class="btn-content">
        <span class="fish-name">{{ fishType }}</span>
        <span class="catch-count">{{ catchCount }}</span>
      </div>
    </button>

    <!-- Notes prompt overlay -->
    <div class="notes-overlay" *ngIf="showNotesPrompt">
      <div class="notes-card" *ngIf="!notesInputVisible">
        <p class="notes-prompt-label">Add notes? <span class="countdown">{{ countdownSeconds }}s</span></p>
        <div class="notes-prompt-actions">
          <button class="notes-yes-btn" (click)="openNotesInput()">Add Notes</button>
          <button class="notes-no-btn" (click)="dismissPrompt()">No Notes</button>
        </div>
      </div>

      <div class="notes-card notes-card--input" *ngIf="notesInputVisible">
        <p class="notes-prompt-label">Catch #{{ pendingCatchId }} — Notes</p>
        <app-resizable-textarea
          [(ngModel)]="notesText"
          placeholder="Bait, lure, technique, conditions..."
          [minRows]="4"
          [autofocus]="true">
        </app-resizable-textarea>
        <div class="notes-input-actions">
          <button class="notes-save-btn" (click)="saveNotes()" [disabled]="isSaving">
            {{ isSaving ? 'Saving…' : 'Save' }}
          </button>
          <button class="notes-no-btn" (click)="dismissPrompt()">Skip</button>
        </div>
      </div>
    </div>
  `,
  styles: [`
    .custom-fish-button {
      margin: 4px;
      padding: 6px 16px;
      height: auto !important;
      min-height: 52px;
      background: var(--fish-bg-panel);
      color: var(--fish-fg);
      border: 1px solid var(--fish-border-hi);
      box-shadow: 2px 2px 0px var(--fish-shadow);
      border-radius: 4px !important;
      transition: none;
    }

    .btn-content {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      gap: 12px;
    }

    .fish-name {
      font-size: 1.1rem;
      font-weight: 800;
      letter-spacing: 0.5px;
      color: var(--fish-fg);
      text-transform: uppercase;
    }

    .catch-count {
      font-size: 1.6rem;
      font-weight: 900;
      background: var(--fish-fg);
      color: var(--fish-bg-panel);
      padding: 0px 8px;
      border-radius: 4px;
    }

    .custom-fish-button:active {
      transform: translate(2px, 2px) !important;
      box-shadow: none !important;
    }

    /* Notes overlay — fixed, centered, full screen backdrop */
    .notes-overlay {
      position: fixed;
      inset: 0;
      background: rgba(0, 0, 0, 0.65);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
    }

    .notes-card {
      background: #1a1a1a;
      border: 1px solid rgba(255,255,255,0.15);
      border-radius: 8px;
      padding: 24px;
      width: min(340px, 90vw);
      text-align: center;
    }

    .notes-card--input {
      text-align: left;
    }

    .notes-prompt-label {
      color: rgba(255,255,255,0.85);
      font-size: 1rem;
      margin: 0 0 16px;
    }

    .countdown {
      color: rgba(255,255,255,0.4);
      font-size: 0.85rem;
      margin-left: 6px;
    }

    .notes-prompt-actions,
    .notes-input-actions {
      display: flex;
      gap: 10px;
      justify-content: center;
    }

    .notes-input-actions {
      margin-top: 12px;
    }

    .notes-yes-btn,
    .notes-save-btn {
      background: #ffffff;
      color: #000000;
      border: none;
      border-radius: 4px;
      padding: 10px 24px;
      font-size: 0.9rem;
      font-weight: 700;
      cursor: pointer;

      &:disabled {
        opacity: 0.5;
        cursor: default;
      }
    }

    .notes-no-btn {
      background: none;
      color: rgba(255,255,255,0.45);
      border: 1px solid rgba(255,255,255,0.2);
      border-radius: 4px;
      padding: 10px 20px;
      font-size: 0.9rem;
      cursor: pointer;

      &:hover {
        color: rgba(255,255,255,0.75);
      }
    }

  `],
  standalone: true,
  imports: [CommonModule, FormsModule, MatButtonModule, ResizableTextareaComponent],
})
export class FishCatchComponent implements OnInit, OnDestroy {
  @Input() fishType!: string;
  @Input() isActive: boolean = false;
  @Input() tripId!: number | null;
  catchCount: string | null = null;

  showNotesPrompt = false;
  notesInputVisible = false;
  notesText = '';
  pendingCatchId: number | null = null;
  isSaving = false;
  countdownSeconds = 10;

  private countdownInterval: ReturnType<typeof setInterval> | null = null;
  private dismissTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(private http: HttpClient) {}

  ngOnInit(): void {
    if (this.isActive) {
      this.loadCurrentCatchCount();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['isActive'] && changes['isActive'].currentValue) {
      this.loadCurrentCatchCount();
    }
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  private loadCurrentCatchCount(): void {
    this.http.post<CatchResponse>(
      `${environment.apiUrl}/trip_catch_count`,
      { fish_type: this.fishType }
    ).subscribe({
      next: (response) => { this.catchCount = response.catch_count; },
      error: () => { this.catchCount = '0'; }
    });
  }

  catchFish(): void {
    if (!this.isActive || !this.tripId) return;

    getAccuratePosition()
      .then((position) => {
        const newCatch = {
          type: this.fishType,
          lat: position.coords.latitude,
          long: position.coords.longitude,
          accuracy: position.coords.accuracy,
        };

        this.http.post<CatchResponse>(`${environment.apiUrl}/caught_fish`, newCatch).subscribe({
          next: (response) => {
            this.catchCount = response.catch_count;
            this.startNotesPrompt(response.catch_id);
          },
          error: (err) => { console.error(`Error catching ${this.fishType}:`, err); }
        });
      })
      .catch((error) => { console.error('Geolocation error:', error); });
  }

  private startNotesPrompt(catchId: number): void {
    this.clearTimers();
    this.pendingCatchId = catchId;
    this.notesText = '';
    this.notesInputVisible = false;
    this.isSaving = false;
    this.countdownSeconds = 10;
    this.showNotesPrompt = true;

    this.countdownInterval = setInterval(() => {
      this.countdownSeconds--;
      if (this.countdownSeconds <= 0) {
        this.dismissPrompt();
      }
    }, 1000);

    this.dismissTimeout = setTimeout(() => this.dismissPrompt(), NOTES_TIMEOUT_MS);
  }

  openNotesInput(): void {
    this.clearTimers();
    this.notesInputVisible = true;
  }

  saveNotes(): void {
    if (!this.pendingCatchId || !this.notesText.trim()) {
      this.dismissPrompt();
      return;
    }
    this.isSaving = true;
    this.http.patch(
      `${environment.apiUrl}/catches/${this.pendingCatchId}/notes`,
      { user_notes: this.notesText.trim() }
    ).subscribe({
      next: () => this.dismissPrompt(),
      error: () => this.dismissPrompt()
    });
  }

  dismissPrompt(): void {
    this.clearTimers();
    this.showNotesPrompt = false;
    this.notesInputVisible = false;
    this.notesText = '';
    this.pendingCatchId = null;
    this.isSaving = false;
  }

  private clearTimers(): void {
    if (this.countdownInterval !== null) {
      clearInterval(this.countdownInterval);
      this.countdownInterval = null;
    }
    if (this.dismissTimeout !== null) {
      clearTimeout(this.dismissTimeout);
      this.dismissTimeout = null;
    }
  }
}
